/**
 * AIInterviewReview - Post-interview transcript + entity summary
 */

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  FileText,
  User,
  Sparkles,
  Download,
  ArrowLeft,
  CheckCircle2,
  Clock,
  MessageSquare,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { DOMAIN_COLORS } from '@/lib/constants/domainColors'
import type { AIInterviewMessage, AIInterviewSession, LiveExtractedEntity } from '@ispot/shared'

// ============================================================================
// COMPONENT
// ============================================================================

interface AIInterviewReviewProps {
  session: AIInterviewSession
  messages: AIInterviewMessage[]
  entities: LiveExtractedEntity[]
  onBack: () => void
  onNewInterview: () => void
}

export function AIInterviewReview({
  session,
  messages,
  entities,
  onBack,
  onNewInterview,
}: AIInterviewReviewProps) {
  const conversationMessages = messages.filter(m => m.role !== 'system')
  const userMessages = messages.filter(m => m.role === 'user')
  const duration = session.completedAt && session.createdAt
    ? Math.round((new Date(session.completedAt).getTime() - new Date(session.createdAt).getTime()) / 60000)
    : 0

  // Group entities by domain
  const byDomain = {
    market: entities.filter(e => e.domain === 'market'),
    business: entities.filter(e => e.domain === 'business'),
    solution: entities.filter(e => e.domain === 'solution'),
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="font-display text-heading font-semibold text-fg">
            Interview Complete
          </h2>
          <p className="text-body text-fg-secondary">{session.title}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button variant="outline" size="sm" onClick={onNewInterview}>
            New Interview
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="rounded-md border border-border bg-bg-card p-3 text-center shadow-none">
          <MessageSquare className="mx-auto mb-1 h-4 w-4 text-fg-muted" />
          <div className="text-lg font-semibold text-fg">{conversationMessages.length}</div>
          <div className="text-[11px] text-fg-muted">Messages</div>
        </Card>
        <Card className="rounded-md border border-border bg-bg-card p-3 text-center shadow-none">
          <Clock className="mx-auto mb-1 h-4 w-4 text-fg-muted" />
          <div className="text-lg font-semibold text-fg">{duration}m</div>
          <div className="text-[11px] text-fg-muted">Duration</div>
        </Card>
        <Card className="rounded-md border border-border bg-bg-card p-3 text-center shadow-none">
          <Sparkles className="mx-auto mb-1 h-4 w-4 text-accent-500" />
          <div className="text-lg font-semibold text-fg">{entities.length}</div>
          <div className="text-[11px] text-fg-muted">Entities</div>
        </Card>
        <Card className="rounded-md border border-border bg-bg-card p-3 text-center shadow-none">
          <User className="mx-auto mb-1 h-4 w-4 text-market-600" />
          <div className="text-lg font-semibold text-fg">{userMessages.length}</div>
          <div className="text-[11px] text-fg-muted">Responses</div>
        </Card>
      </div>

      {/* Persona Card */}
      {session.linkedPersonName && (
        <Card className="flex items-center gap-3 rounded-md border border-border bg-bg-card p-4 shadow-none">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-market-50">
            <User className="h-5 w-5 text-market-600" />
          </div>
          <div>
            <div className="text-body font-medium text-fg">{session.linkedPersonName}</div>
            <div className="text-caption text-fg-muted">
              {session.personaAutoCreated ? 'Persona entity auto-created' : 'Linked as Persona entity'}
            </div>
          </div>
          <CheckCircle2 className="ml-auto h-4 w-4 text-success-fg" />
        </Card>
      )}

      <div className="grid grid-cols-3 gap-4">
        {/* Entities by Domain */}
        {(['market', 'business', 'solution'] as const).map(domain => {
          const domainEntities = byDomain[domain]
          const colors = DOMAIN_COLORS[domain]
          return (
            <Card key={domain} className="rounded-md border border-border bg-bg-card p-3 shadow-none">
              <h3 className={cn('text-xs font-medium mb-2 capitalize', colors?.text)}>
                {domain} ({domainEntities.length})
              </h3>
              {domainEntities.length === 0 ? (
                <p className="text-[11px] text-fg-muted">No entities</p>
              ) : (
                <div className="space-y-1">
                  {domainEntities.map((e, i) => (
                    <div
                      key={`${e.type}-${e.name}-${i}`}
                      className={cn('px-2 py-1 rounded text-[11px]', colors?.bg)}
                    >
                      <span className="font-medium">{e.name}</span>
                      <span className={cn('ml-1', colors?.text)}>{e.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )
        })}
      </div>

      {/* Transcript */}
      <Card className="rounded-md border border-border bg-bg-card p-4 shadow-none">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-fg-muted" />
          <h3 className="text-body font-medium text-fg">Transcript</h3>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {conversationMessages.map(msg => (
            <div key={msg.id} className="flex gap-2 text-sm">
              <Badge
                variant="outline"
                className={cn(
                  'flex-shrink-0 text-[10px]',
                  msg.role === 'ai' ? 'border-border' : 'border-border-strong',
                )}
              >
                {msg.role === 'ai' ? 'AI' : session.linkedPersonName || 'User'}
              </Badge>
              <p className="text-fg-secondary leading-relaxed">{msg.content}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
